import React, {
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from "react";

import axios from "axios";

import { Column, ColumnOptionsUtils } from "@greco/components";
import i18next from "i18next";

import { defaultCols } from "pages/OfferList/columns";
import api from "service/api";
import {
  LoadStatus,
  OfferListEntryDto,
  Setting,
} from "library/types/types_old";
import { ExpandedColumn } from "library/types/columns";
import { handleAxiosError } from "library/utils";
import { toast } from "react-toastify";
import { hydrateColumn } from "library/utils/columns";
import { APP_ID } from "library/constants";

const useOffersState = () => {
  // Offer page
  const [columnsValue, setColumnsValue] = useState(defaultCols);
  const [loadStatusValue, setLoadStatusValue] = useState<LoadStatus>("none");

  const [tempColumnsValue, setTempColumnsValue] = useState<
    ExpandedColumn[] | null
  >(defaultCols);
  const [offersValue, setOffersValue] = useState<{
    offers: OfferListEntryDto[];
    count: number;
  }>({
    offers: [],
    count: 0,
  });

  const [sortValue, setSortValue] = useState({
    key: "",
    dir: "",
  });
  const areAnyColumnsFilteredValue = useMemo(() => {
    return columnsValue.some((c) =>
      ColumnOptionsUtils.isColumnFiltered(c.filter)
    );
  }, [columnsValue]);

  const [selectedViewIdValue, setSelectedViewIdValue] = useState<
    Setting["userAppSettingId"] | null
  >(null);

  const [settingsValue, setSettingsValue] = useState<Setting[]>([]);

  const [selectedViewValue, setSelectedViewValue] = useState<
    Setting | null | undefined
  >(null);

  const [selectedViewColumnsValue, setSelectedViewColumnsValue] = useState<
    ExpandedColumn[] | null
  >(null);

  useEffect(() => {
    const selectedView = settingsValue.find(
      (s) => s.userAppSettingId === selectedViewIdValue
    );
    setSelectedViewValue(selectedView);
  }, [selectedViewIdValue, settingsValue]);

  useEffect(() => {
    if (!selectedViewValue) {
      setSelectedViewColumnsValue(defaultCols);
      return;
    }
    const columns = JSON.parse(
      selectedViewValue.userAppSettingValue
    ) as ExpandedColumn[];
    const mappedColumns = columns.map((c) =>
      hydrateColumn({ column: c, defaultColumns: defaultCols })
    );
    setSelectedViewColumnsValue(mappedColumns);
    setColumnsValue(mappedColumns);
  }, [selectedViewValue]);

  useEffect(() => {
    setTempColumnsValue(selectedViewColumnsValue);
  }, [selectedViewColumnsValue]);

  const [searchValue, setSearchValue] = useState("");

  let cancelSource;

  const columnOptionsSettingsValue = useMemo(() => {
    return settingsValue.filter((s) => {
      return (
        s.appSettingCodeId === 900000000010303 && s.applicationCodeId === APP_ID
      );
    });
  }, [settingsValue]);

  const [invalidateOfferDialog, setInvalidateOfferDialog] = useState({
    isOpen: false,
    offer: null,
  });

  const loadSettings = async () => {
    try {
      //setLoadStatusValue('loading');
      const res = await api.getAppSettings(APP_ID);
      setSettingsValue(res.data);
      //setLoadStatusValue('success');
      // default view
      const defaultView = res.data.find((s) => s.isDefault);
      if (defaultView) {
        setSelectedViewIdValue(defaultView.userAppSettingId);
        // const columns = selectedViewColumnsValue;
        // setTempColumnsValue(columns);
        // setColumnsValue(columns);
      }
    } catch (err) {
      handleAxiosError(err);
      //setLoadStatusValue("error");
    }
  };

  const invalidateOffer = async (args: any) => {
    const { params, onSuccess, onError } = args as any;
    try {
      setLoadStatusValue("loading");
      await api.invalidateOffer(params);
      loadOffers({});
      setLoadStatusValue("success");
      onSuccess && onSuccess();
      toast.success(i18next.t("greco.success"));
    } catch (err) {
      handleAxiosError(err);
      setLoadStatusValue("error");
      onError && onError();
    }
  };

  const createSetting = async (newSetting: Setting) => {
    try {
      setLoadStatusValue("loading");
      const res = await api.addAppSetting(newSetting);
      setLoadStatusValue("success");
      const allSettings = res.data;
      setSettingsValue(allSettings);
      const newSettingId = allSettings.find(
        (s) => s.userAppSettingName === newSetting.userAppSettingName
      )?.userAppSettingId;
      if (newSettingId) {
        setSelectedViewIdValue(newSettingId);
      }
      toast.success(i18next.t("greco.success"));
    } catch (err) {
      handleAxiosError(err);
      setLoadStatusValue("error");
    }
  };

  const deleteSetting = async (id: number) => {
    try {
      setLoadStatusValue("loading");
      const res = await api.deleteAppSetting(id);
      const allSettings = res.data;
      setSettingsValue(allSettings);
      setLoadStatusValue("success");
      // set(selectedViewIdAtom, null);
      toast.success(i18next.t("greco.success"));
    } catch (err) {
      handleAxiosError(err);
      setLoadStatusValue("error");
    }
  };

  const updateSetting = async (setting: Setting) => {
    try {
      setLoadStatusValue("loading");
      const res = await api.updateAppSetting(setting);
      const allSettings = res.data;
      setSettingsValue(allSettings);
      setLoadStatusValue("success");
      toast.success(i18next.t("greco.success"));
    } catch (err) {
      handleAxiosError(err);
      setLoadStatusValue("error");
    }
  };

  const updateColumnWidth = (column: Column, width: number) => {
    const newColumns = [...columnsValue];
    const index = newColumns.findIndex((c) => c.key === column.key);
    // Ensure the column exists before modifying it
    if (index !== -1 && newColumns && newColumns[index]) {
      newColumns[index]!.width = width; // Use the passed width parameter instead of column.width
    }
    setColumnsValue(newColumns);
  };

  const loadOffers = async (args) => {
    const { reset = true, delay = false, skip = 0, top = 45 } = args as any;
    // if (delay) {
    //   await sleep(300);
    // }
    if (cancelSource && reset) {
      cancelSource.cancel();
    }
    cancelSource = axios.CancelToken.source();
    // if (reset) {
    //   setOffersValue({
    //     count: 0,
    //     offers: []
    //   });
    // }
    try {
      //setLoadStatusValue('loading');
      const res = await api.searchOffers({
        cancelToken: cancelSource.token,
        columns: columnsValue,
        skip,
        top,
        search: searchValue,
        sort: sortValue,
      });
      //setLoadStatusValue('success');
      const currentOffers = offersValue.offers;
      let newOffers = res.data.value;
      if (reset === false) {
        newOffers = [...currentOffers, ...res.data.value];
      }
      setOffersValue({
        count: res.data["@odata.count"],
        offers: newOffers,
      });
    } catch (err) {
      // console.log(err, );
      // handleAxiosError(err);
      //setLoadStatusValue('error');
    }
  };

  // useEffect(() => {
  //   loadOffers({});
  // }, [columnsValue]);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    loadOffers({ delay: true });
  }, [searchValue, sortValue, columnsValue]);

  return {
    updateColumnWidth,
    columnsValue,
    offersValue,
    sortValue,
    searchValue,
    areAnyColumnsFilteredValue,
    invalidateOffer,

    selectedViewColumnsValue,
    selectedViewIdValue,
    selectedViewValue,
    tempColumnsValue,
    columnOptionsSettingsValue,
    createSetting,
    deleteSetting,
    loadSettings,
    updateSetting,
    invalidateOfferDialog,
    setInvalidateOfferDialog,
    setSelectedViewIdValue,
    setTempColumnsValue,
    setColumnsValue,
    setColumns: (val) => setColumnsValue(val),
    setOffers: (val) => setOffersValue(val),
    setSort: (val) => setSortValue(val),
    setSearch: (val) => setSearchValue(val),
    loadOffers: async (args) => loadOffers(args),
  };
};

interface OffersContextType {
  updateColumnWidth: (column: Column, width: number) => void;
  columnsValue: ExpandedColumn[];
  offersValue: { offers: any[]; count: number };
  sortValue: { key: string; dir: string };
  searchValue: string;
  areAnyColumnsFilteredValue: boolean;
  invalidateOffer: (args: any) => Promise<void>;
  selectedViewColumnsValue: ExpandedColumn[] | null;
  selectedViewIdValue: Setting["userAppSettingId"] | null;
  selectedViewValue: Setting | null | undefined;
  tempColumnsValue: ExpandedColumn[] | null;
  columnOptionsSettingsValue: Setting[];
  createSetting: (newSetting: Setting) => Promise<void>;
  deleteSetting: (id: number) => Promise<void>;
  loadSettings: () => Promise<void>;
  updateSetting: (setting: Setting) => Promise<void>;
  invalidateOfferDialog: { isOpen: boolean; offer: any };
  setInvalidateOfferDialog: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; offer: any }>
  >;
  setSelectedViewIdValue: React.Dispatch<
    React.SetStateAction<Setting["userAppSettingId"] | null>
  >;
  setTempColumnsValue: React.Dispatch<
    React.SetStateAction<ExpandedColumn[] | null>
  >;
  setColumnsValue: React.Dispatch<React.SetStateAction<ExpandedColumn[]>>;
  setColumns: (val: ExpandedColumn[]) => void;
  setOffers: (val: { offers: any[]; count: number }) => void;
  setSort: (val: { key: string; dir: string }) => void;
  setSearch: (val: string) => void;
  loadOffers: (args: any) => Promise<void>;
}

const OffersContext = createContext<OffersContextType | null>(null);

export const OffersContextProvider = ({ children }) => (
  <OffersContext.Provider value={useOffersState()}>
    {children}
  </OffersContext.Provider>
);

export const useColumns = () =>
  (useContext(OffersContext) as any)?.columnsValue;
export const useOffers = () => (useContext(OffersContext) as any)?.offersValue;
export const useSort = () => (useContext(OffersContext) as any)?.sortValue;
export const useSearch = () => (useContext(OffersContext) as any)?.searchValue;

export const useAreAnyColumnsFilteredValue = () =>
  (useContext(OffersContext) as any)?.areAnyColumnsFilteredValue;

export const useSetColumns = () =>
  (useContext(OffersContext) as any)?.setColumns;
export const useSetSort = () => (useContext(OffersContext) as any)?.setSort;
export const useSetSearch = () => (useContext(OffersContext) as any)?.setSearch;

export const useLoadOffers = () =>
  (useContext(OffersContext) as any)?.loadOffers;

export const useColumnsValue = () =>
  (useContext(OffersContext) as any)?.columnsValue;
export const useSelectedViewColumnsValue = () =>
  (useContext(OffersContext) as any)?.selectedViewColumnsValue;
export const useSelectedViewIdValue = () =>
  (useContext(OffersContext) as any)?.selectedViewIdValue;
export const useSetSelectedViewIdValue = () =>
  (useContext(OffersContext) as any)?.setSelectedViewIdValue;
export const useSetTempColumnsValue = () =>
  (useContext(OffersContext) as any)?.setTempColumnsValue;

export const useSetColumnsValue = () =>
  (useContext(OffersContext) as any)?.setColumnsValue;

export const useSelectedViewValue = () =>
  (useContext(OffersContext) as any)?.selectedViewValue;
export const useTempColumnsValue = () =>
  (useContext(OffersContext) as any)?.tempColumnsValue;

export const useColumnOptionsSettingsValue = () =>
  (useContext(OffersContext) as any)?.columnOptionsSettingsValue;
export const useCreateSetting = () =>
  (useContext(OffersContext) as any)?.createSetting;
export const useDeleteSetting = () =>
  (useContext(OffersContext) as any)?.deleteSetting;

export const useLoadSettings = () =>
  (useContext(OffersContext) as any)?.loadSettings;

export const useUpdateSetting = () =>
  (useContext(OffersContext) as any)?.updateSetting;

export const useInvalidateOfferDialog = () =>
  (useContext(OffersContext) as any)?.invalidateOfferDialog;

export const useSetInvalidateOfferDialog = () =>
  (useContext(OffersContext) as any)?.setInvalidateOfferDialog;

export const useInvalidateOffer = () =>
  (useContext(OffersContext) as any)?.invalidateOffer;

export const useUpdateColumnWidth = () =>
  (useContext(OffersContext) as any)?.updateColumnWidth;

export const useSetLoadStatusValue = () =>
  (useContext(OffersContext) as any)?.setLoadStatusValue;

export const useLoadStatusValue = () =>
  (useContext(OffersContext) as any)?.loadStatusValue;
